import {Decimal} from 'decimal.js';

import {replaceAll} from 'util/NameUtil';

import {localeAwareFormatNumber} from './NumberFormattingUtil';
import {
  DEFAULT_NUMBER_PARSING_FORMAT,
  GROUP_DELIMITERS_TO_SUPPORTED_ALTERNATIVES,
  NumberParsingFormat,
} from './NumberParsingFormat';

/**
 * Tries to parse a string number according to a given numberParsingFormat.
 * Returns the parsed number as a string or returns null if it fails to parse the number.
 * Parses with default number parsing format if shadowMode = True
 */
export function tryParseNumberWithUserNumberFormat(numberToParse: string): string | null {
  const numberParsingFormat = window.bootstrap
    ? window.bootstrap.numberParsingFormat
    : DEFAULT_NUMBER_PARSING_FORMAT;
  try {
    return parseNumberWithNumberFormatToString(numberToParse, numberParsingFormat);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch {
    return null;
  }
}

/**
 * Parses a string number given a number parsing format with group delimiter, decimal delimiter, and number pattern.
 * Removes leading/trailing 0's before parsing.
 * Supports scientific notation.
 * Some locales support alternative group delimiters as defined in GROUP_DELIMITERS_TO_SUPPORTED_ALTERNATIVES
 * Validates that the number is correctly parsed before returning the value, which can be caused by incorrect delimiter placements.
 * Ex. 1,234.56 for de-DE does not get incorrectly parsed as 1.23456
 */
export function parseNumberWithNumberFormat(
  numberToParse: string,
  numberParsingFormat: NumberParsingFormat
): number {
  const sanitizedNumberToParse = _sanitizeNumberToParse(numberToParse, numberParsingFormat);
  return maybeScientificNotation(sanitizedNumberToParse)
    ? parseScientificNotation(sanitizedNumberToParse, numberParsingFormat)
    : parseStandardNotation(sanitizedNumberToParse, numberParsingFormat);
}

/**
 * Parses a string number given a number parsing format with group delimiter, decimal delimiter, and number pattern.
 * Verifies that the numberToParse is a valid number according to the given locale.
 * If numberToParse is a valid number, returns numberToParse with its delimiters replaced according to "standard" notation
 * (period decimal delimiter and no group delimiters) as a string.
 * This method allows all digits of numberToParse to be preserved, whereas coercing to a float or int may result in lost digits.
 */
export function parseNumberWithNumberFormatToString(
  numberToParse: string,
  numberParsingFormat: NumberParsingFormat
): string {
  const isValid = isNumberToParseValid(numberToParse, numberParsingFormat);
  if (isValid) {
    return replaceDelimiters(convertGroupDelimiters(numberToParse, numberParsingFormat), numberParsingFormat);
  } else {
    throw new Error(`${numberToParse} is not a valid number`);
  }
}

export function isNumberToParseValid(
  numberToParse: string,
  numberParsingFormat: NumberParsingFormat
): boolean {
  let isValid = true;
  try {
    parseNumberWithNumberFormat(numberToParse, numberParsingFormat);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch {
    isValid = false;
  }
  return isValid;
}

/**
 * Removes underscores to ignore them when parsing with locale since python allows underscores in numeric literals.
 * Throws error if number_to_parse includes double underscores, since python does not allow double underscores in numeric literals.
 */
function removeUnderscores(numberToParse: string): string {
  if (numberToParse.includes('__')) {
    throw new Error(`${numberToParse} is not a valid number`);
  }
  return replaceAll(numberToParse, '_', '');
}

function _sanitizeNumberToParse(numberToParse: string, numberParsingFormat: NumberParsingFormat): string {
  const sanitizedNumberToParse = convertGroupDelimiters(numberToParse, numberParsingFormat);
  const sanitizedNumberToParseNoUnderscores = removeUnderscores(sanitizedNumberToParse);
  // converting to lowercase to allow checking for lowercase "e" for scientific notation
  return sanitizedNumberToParseNoUnderscores.toLowerCase();
}

/**
 * Converts group delimiters to a group delimiter that babel supports.
 * We currently make exceptions for certain group delimiters depending on the number parsing format.
 * Some locales support alternative group delimiters as defined in GROUP_DELIMITERS_TO_SUPPORTED_ALTERNATIVES.
 */
function convertGroupDelimiters(numberToParse: string, numberParsingFormat: NumberParsingFormat): string {
  if (GROUP_DELIMITERS_TO_SUPPORTED_ALTERNATIVES[numberParsingFormat.groupDelimiter] != null) {
    return replaceAll(
      numberToParse,
      GROUP_DELIMITERS_TO_SUPPORTED_ALTERNATIVES[numberParsingFormat.groupDelimiter],
      numberParsingFormat.groupDelimiter
    );
  }
  return numberToParse;
}

function maybeScientificNotation(numberToParse: string): boolean {
  return numberToParse.includes('e');
}

function parseScientificNotation(numberToParse: string, numberParsingFormat: NumberParsingFormat): number {
  const sanitizedNumberToParse = sanitizeScientificNotation(numberToParse, numberParsingFormat);
  return new Decimal(sanitizedNumberToParse).toNumber();
}

function sanitizeScientificNotation(numberToParse: string, numberParsingFormat: NumberParsingFormat): string {
  const numberParts = numberToParse.split('e');
  if (numberParts.length !== 2) {
    throw new Error(`${numberToParse} is not a valid scientific notation number`);
  }
  const [beforeE, afterE] = numberParts;
  if (afterE.match(/^[+-]?\d*$/g) == null) {
    throw new Error(`${numberToParse} is not a valid scientific notation number`);
  }
  // validates the left side of 'e'
  parseStandardNotation(beforeE, numberParsingFormat);

  return `${replaceDelimiters(beforeE, numberParsingFormat)}e${afterE}`;
}

function parseStandardNotation(numberToParse: string, numberParsingFormat: NumberParsingFormat): number {
  numberToParse = _sanitizeStandardNotation(numberToParse, numberParsingFormat);
  const delimitersReplaced = replaceDelimiters(numberToParse, numberParsingFormat);
  let parsed;
  try {
    parsed = new Decimal(delimitersReplaced);
  } catch {
    throw new Error(`${numberToParse} is not a valid decimal number`);
  }

  validateParsedNumber(parsed, numberToParse, numberParsingFormat);
  return parsed.toNumber();
}

export function _sanitizeStandardNotation(
  numberToParse: string,
  numberParsingFormat: NumberParsingFormat
): string {
  const [maybeSign, beforeDecimal, decimalAndAfterDecimal] = tryMatchNumberToParse(
    numberToParse,
    numberParsingFormat
  );
  const sanitizedBeforeDecimal = sanitizeBeforeDecimal(beforeDecimal);
  const sanitiziedDecimalAndAfterDecimal = sanitizeAfterDecimal(
    decimalAndAfterDecimal,
    numberParsingFormat.decimalDelimiter
  );
  const sanitizedNumber = addLeadingZero(
    sanitizedBeforeDecimal + sanitiziedDecimalAndAfterDecimal,
    numberParsingFormat.decimalDelimiter
  );
  if (sanitizedNumber === '') {
    return '0';
  }

  return maybeSign + sanitizedNumber;
}

function tryMatchNumberToParse(
  numberToParse: string,
  numberParsingFormat: NumberParsingFormat
): Array<string> {
  // Match the sign, if it exists
  const maybeSignGroup = '([-+]?)';

  // Match the digits + group delimiters to the left of (but not including) the decimal, if it exists
  const maybeBeforeDecimalGroup = `([\\d${numberParsingFormat.groupDelimiter}]*)?`;

  // Match the digits + delimiters to the right of the decimal including the decimal, if it exists
  const maybeDecimalAndAfterDecimalGroup = `([${numberParsingFormat.decimalDelimiter}${numberParsingFormat.groupDelimiter}\\d]*)?`;

  const numberToParseRe = new RegExp(
    `^${maybeSignGroup}${maybeBeforeDecimalGroup}${maybeDecimalAndAfterDecimalGroup}$`,
    'g'
  );
  const numberMatch = numberToParseRe.exec(numberToParse);
  if (numberMatch == null || numberToParse === '') {
    throw new Error(`${numberToParse} is not a valid decimal number`);
  }

  return [numberMatch[1], numberMatch[2], numberMatch[3]];
}

function sanitizeBeforeDecimal(beforeDecimal: string): string {
  return beforeDecimal ? beforeDecimal.replace(/^0+/g, '') : '';
}

function sanitizeAfterDecimal(afterDecimal: string, decimalDelimiter: string): string {
  const afterDecimalRe = new RegExp(`[${decimalDelimiter}]+$`, 'g');
  return afterDecimal ? afterDecimal.replace(/0+$/g, '').replace(afterDecimalRe, '') : '';
}

function addLeadingZero(numberStr: string, decimalDelimiter: string): string {
  if (numberStr.charAt(0) === decimalDelimiter) {
    return `0${numberStr}`;
  } else {
    return numberStr;
  }
}

function replaceDelimiters(numberToParse: string, numberParsingFormat: NumberParsingFormat): string {
  const groupRe = new RegExp(`[${numberParsingFormat.groupDelimiter}]`, 'g');
  const decimalRe = new RegExp(`[${numberParsingFormat.decimalDelimiter}]`, 'g');
  return numberToParse.replace(groupRe, '').replace(decimalRe, '.');
}

/**
 * Validates the parsed result to ensure it was parsed correctly. The parsed result is reformatted back into a string
 * according to the number parsing format, then compared against the original string.
 */
function validateParsedNumber(
  parsedResult: Decimal,
  originalInput: string,
  numberParsingFormat: NumberParsingFormat
): void {
  const includeGroupDelimiter = originalInput.includes(numberParsingFormat.groupDelimiter);
  const parsedResultInNumberFormat = localeAwareFormatNumber(
    parsedResult,
    numberParsingFormat,
    includeGroupDelimiter
  );
  originalInput = sanitizeOriginalInputForValidation(originalInput);
  if (parsedResultInNumberFormat !== originalInput) {
    throw new Error(`${originalInput} is incorrectly formatted`);
  }
}

function sanitizeOriginalInputForValidation(originalInput: string): string {
  /**
   * Santizes original input to validate the parsed number.
   * We do this since the parsed number in number format is not guaranteed to be the same as the original input as is.
   * The original input may contain a '+', while the parsed number in number format will not.
   */
  return originalInput.replace(/^\+/g, '');
}
